import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DocumentTextIcon, ChevronRightIcon, ChevronLeftIcon } from '@heroicons/react/outline';
import { getContractsForUser } from '../../services/contractService';
import useAuthStore from '../../store/authStore';
import { Contract } from '../../services/contractService';

const ContractsPage: React.FC = () => {
  const navigate = useNavigate();
  const { userData } = useAuthStore();
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchContracts = async () => {
      if (!userData?.uid || !userData?.role) {
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      setError(null);
      try {
        const userContracts = await getContractsForUser(userData.uid, userData.role);
        setContracts(userContracts);
      } catch (err) {
        console.error('Error fetching contracts:', err);
        setError('حدث خطأ أثناء جلب العقود. يرجى المحاولة مرة أخرى.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchContracts();
  }, [userData]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'نشط':
        return 'bg-green-100 text-green-800';
      case 'معلق':
      case 'قيد الانتظار':
        return 'bg-yellow-100 text-yellow-800';
      case 'منتهي':
      case 'ملغي':
        return 'bg-gray-50 text-gray-800';
      case 'مكتمل':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const formatDate = (date: any) => {
    if (!date) return '';
    const jsDate = date.toDate ? date.toDate() : new Date(date);
    return jsDate.toLocaleDateString('en-SA');
  };

  const activeContracts = contracts.filter(c => ['نشط', 'معلق', 'قيد الانتظار'].includes(c.status));
  const completedContracts = contracts.filter(c => ['مكتمل', 'منتهي', 'ملغي'].includes(c.status));

  const renderContractTable = (title: string, contractList: Contract[]) => (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-24">
      <div className="p-4 border-b border-gray-50">
        <h2 className="font-semibold text-gray-800">{title}</h2>
      </div>
      {contractList.length === 0 ? (
        <div className="p-8 text-center text-gray-500">
          <p>لا توجد عقود في هذا القسم</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-50">
          {contractList.map((contract) => (
            <div 
              key={contract.id}
              onClick={() => navigate(`/contracts/${contract.id}`)}
              className="p-4 active:bg-gray-50 transition-colors duration-150 cursor-pointer"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-medium text-gray-900 truncate">
                      {contract.title}
                    </h3>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(contract.status)}`}>
                      {contract.status}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    {contract.lawyerName || 'علي الزهراني'}
                  </p>
                  <div className="mt-2 flex items-center justify-between">

                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      // وظيفة وهمية لتحميل PDF
                      console.log('تحميل عقد PDF:', contract.id);
                    }}
                    className="text-blue-600 hover:text-blue-800 p-1 rounded-full hover:bg-blue-50"
                    title="تحميل العقد"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </button>
                </div>
                </div>
                <div className="mr-3 flex-shrink-0 self-center">
                  <ChevronRightIcon className="h-5 w-5 text-gray-400 transform rotate-180" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (isLoading) {
    return (
      <div className="p-5 bg-gray-50 min-h-screen flex justify-center items-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-5 bg-gray-50 min-h-screen">
        <div className="bg-red-50 p-4 rounded-lg">
          <p className="text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-5 bg-gray-50 min-h-screen font-sans text-right dir-rtl">
      <div className="flex items-center mb-6">
        <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-gray-200">
          <ChevronLeftIcon className="h-5 w-5 text-gray-700 rotate-180" />
        </button>
        <h2 className="text-xl font-bold text-gray-900 mr-4">العقود</h2>
      </div>

      <main>
        {contracts.length === 0 && !isLoading ? (
          <div className="text-center py-12">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">لا توجد عقود</h3>
            <p className="mt-1 text-sm text-gray-500">لا توجد عقود لعرضها حالياً.</p>
          </div>
        ) : (
          <>
            {renderContractTable('العقود النشطة', activeContracts)}
            {renderContractTable('العقود المكتملة', completedContracts)}
          </>
        )}
      </main>
    </div>
  );
};

export default ContractsPage;
